import React, { useState } from 'react';
import ReactECharts from 'echarts-for-react';
import {
    Grid,
    Typography,
    Paper,
    Box,
    Skeleton,
    Alert,
    AlertTitle,
    Tooltip,
    IconButton,
    LinearProgress
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HouseMonitoringMetricsBoxes from './HouseMonitoringMetricsBoxes';

function HouseMonitoringAdditional({ id, loadingInsights, dayInsights, weekInsights, currentCost, averageCost, monthInsights }) {
    const theme = useTheme();
    const [viewIndex, setViewIndex] = useState(0); // 0: Metrics Boxes, 1: Pie Charts
    const handleToggleView = (direction) => {
        setViewIndex((prev) => {
            if (direction === 'left') return prev === 0 ? 1 : 0;
            if (direction === 'right') return prev === 1 ? 0 : 1;
            return prev;
        });
    };
    const handleDotClick = (index) => setViewIndex(index);
    // Percentage calculations
    const todayPercentage = dayInsights?.data && typeof dayInsights.data.current === 'number' && typeof dayInsights.data.average === 'number' && dayInsights.data.average !== 0
        ? (dayInsights.data.current / dayInsights.data.average) * 100
        : 0;
    const weeklyPercentage = weekInsights?.data && typeof weekInsights.data.current === 'number' && typeof weekInsights.data.average === 'number' && weekInsights.data.average !== 0
        ? (weekInsights.data.current / weekInsights.data.average) * 100
        : 0;

    // Gradient colors for pie charts
    const gradientColor = {
        measured: {
            type: 'linear',
            x: 0, y: 0, x2: 1, y2: 1,
            colorStops: [
                { offset: 0, color: theme.palette.secondary.main },
                { offset: 1, color: theme.palette.primary.main }
            ]
        },
        remaining: theme.palette.grey[300]
    };

    // Today's Consumption Pie Chart
    const todayDoughnutOptions = {
        tooltip: {
            trigger: 'item',
            formatter: params => params.name === 'Consumption' ? `Consumption today vs Average Daily Consumption: ${params.value}%` : ''
        },
        series: [
            {
                name: "Today's Consumption",
                type: 'pie',
                radius: ['50%', '70%'],
                label: { show: false },
                labelLine: { show: false },
                data: [
                    {
                        value: todayPercentage.toFixed(2),
                        name: 'Consumption',
                        itemStyle: { color: gradientColor.measured }
                    },
                    {
                        value: (100 - todayPercentage).toFixed(2),
                        name: 'Daily Average',
                        itemStyle: { color: gradientColor.remaining }
                    }
                ]
            }
        ]
    };

    // Weekly Consumption Pie Chart
    const weeklyDoughnutOptions = {
        tooltip: {
            trigger: 'item',
            formatter: params => params.name === 'Consumption' ? `Consumption this week vs Average Weekly Consumption: ${params.value}%` : ''
        },
        series: [
            {
                name: "Weekly Consumption",
                type: 'pie',
                radius: ['50%', '70%'],
                label: { show: false },
                labelLine: { show: false },
                data: [
                    {
                        value: weeklyPercentage.toFixed(2),
                        name: 'Consumption',
                        itemStyle: { color: gradientColor.measured }
                    },
                    {
                        value: (100 - weeklyPercentage).toFixed(2),
                        name: 'Weekly Average',
                        itemStyle: { color: gradientColor.remaining }
                    }
                ]
            }
        ]
    };


    return (
        <Paper
            elevation={0}
            style={{
                padding: '20px',
                borderRadius: '25px',
                backgroundColor: theme.palette.common.white,
                minHeight: 700,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
            }}
        >
            {viewIndex === 0 ? (
                <>
                    <Typography
                        variant="h5"
                        align="center"
                        sx={{ fontWeight: 'bold' }}
                        className={'fancyText'}
                    >
                        Consumption Overview
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 320, width: '100%', flexGrow: 1 }}>
                        <Grid container spacing={1} alignItems="center" sx={{ height: '100%' }}>
                            {/* Today's Consumption Doughnut Chart */}
                            <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                {dayInsights?.error ? (
                                    <Alert severity="error" sx={{ mt: 2 }}>
                                        <AlertTitle>Error</AlertTitle>
                                        Something went wrong with today's data. Please try again later.
                                    </Alert>
                                ) : <>
                                    {!todayPercentage ? <Alert severity="info" sx={{ mt: 2 }}>
                                        <AlertTitle>Gathering data</AlertTitle>
                                        Please come back later for more detailed insights.
                                    </Alert>
                                        : <Box display="flex" flexDirection="column" alignItems="center">
                                            <ReactECharts option={todayDoughnutOptions} style={{ height: '250px', width: '100%' }} />
                                            {loadingInsights ? (
                                                <Skeleton variant="rectangular" width="100%" height={30}
                                                    sx={{ borderRadius: '25px' }} />
                                            ) : (
                                                <>
                                                    <Typography align="center" variant="h6" display="flex" alignItems="center">
                                                        <strong>{dayInsights?.data && typeof dayInsights.data.current === 'number' ? dayInsights.data.current : '-'} kWh &nbsp;</strong>
                                                    </Typography>
                                                    <Typography align="center" variant="body1" display="flex" alignItems="center">
                                                        (<strong style={{
                                                            color: todayPercentage > 80 ? '#d32f2f' : todayPercentage > 50 ? '#ED6C02' : 'inherit',
                                                        }}>
                                                            {todayPercentage.toFixed(1)}%
                                                        </strong> &nbsp;of average)
                                                        <Tooltip
                                                            title="Consumption today compared to last month's average daily consumption"
                                                            arrow>
                                                            <IconButton size="small">
                                                                <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>}
                                </>}
                            </Grid>

                            {/* Weekly Consumption Doughnut Chart */}
                            <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                {weekInsights?.error ? (
                                    <Alert severity="error" sx={{ mt: 2 }}>
                                        <AlertTitle>Error</AlertTitle>
                                        Something went wrong with this week's data. Please try again later.
                                    </Alert>
                                ) : (
                                    <Box display="flex" flexDirection="column" alignItems="center">
                                        <ReactECharts option={weeklyDoughnutOptions} style={{ height: '250px', width: '100%' }} />
                                        {loadingInsights ? (
                                            <Skeleton variant="rectangular" width="100%" height={30} sx={{ borderRadius: '25px' }} />
                                        ) : (
                                            <>
                                                <Typography align="center" variant="h6" display="flex" alignItems="center">
                                                    <strong>{weekInsights?.data && typeof weekInsights.data.current === 'number' ? weekInsights.data.current : '-'} kWh &nbsp;</strong>
                                                </Typography>
                                                <Typography align="center" variant="body1" display="flex" alignItems="center">
                                                    (<strong style={{
                                                        color: weeklyPercentage > 80 ? '#d32f2f' : weeklyPercentage > 50 ? '#ED6C02' : 'inherit',
                                                    }}>
                                                        {weeklyPercentage.toFixed(1)}%
                                                    </strong> &nbsp;of average)
                                                    <Tooltip
                                                        title="Consumption this week compared to last week's average total consumption"
                                                        arrow>
                                                        <IconButton size="small">
                                                            <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Typography>
                                            </>
                                        )}
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                        {/* Spacer to push progress bars to the bottom */}
                        <Box sx={{ flexGrow: 1 }} />
                        {/* Progress Bars Section */}
                        <Box sx={{ width: '100%', mt: 3 }}>
                            {/* Only show if monthInsights.data.average is valid and nonzero */}
                            {monthInsights && monthInsights.data && typeof monthInsights.data.average === 'number' && monthInsights.data.average !== 0 && (
                                <>
                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.min((monthInsights.data.current / monthInsights.data.average) * 100, 100)}
                                        sx={{
                                            width: '100%',
                                            height: 12,
                                            borderRadius: 6,
                                            backgroundColor: theme.palette.grey[200],
                                            '& .MuiLinearProgress-bar': {
                                                backgroundImage: `linear-gradient(to right, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                                            },
                                        }}
                                    />
                                    <Typography variant="body1" align="center" sx={{ mt: 1 }}>
                                        Consumption until today is <strong>{monthInsights.data.current} kWh</strong> while monthly average is <strong>{monthInsights.data.average} kWh</strong> (
                                        <strong style={{ color: (monthInsights.data.current / monthInsights.data.average) * 100 > 80 ? '#d32f2f' : 'inherit' }}>{((monthInsights.data.current / monthInsights.data.average) * 100).toFixed(1)}%</strong>
                                        )
                                        <Tooltip title="Consumption this month compared to last months' average total consumption" arrow>
                                            <IconButton size="small">
                                                <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Typography>
                                </>
                            )}

                            {averageCost !== 0 && (
                                <>
                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.min((currentCost / averageCost) * 100, 100)}
                                        sx={{
                                            width: '100%',
                                            height: 12,
                                            borderRadius: 6,
                                            mt: 4,
                                            backgroundColor: theme.palette.grey[200],
                                            '& .MuiLinearProgress-bar': {
                                                backgroundImage: `linear-gradient(to right, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                                            },
                                        }}
                                    />
                                    <Typography variant="body1" align="center" sx={{ mt: 1 }}>
                                        Cost until today is <strong>€{Number(currentCost).toFixed(2)}</strong> while monthly average is <strong>€{Number(averageCost).toFixed(2)}</strong> (
                                        <strong style={{ color: (currentCost / averageCost) * 100 > 80 ? '#d32f2f' : 'inherit' }}>{((currentCost / averageCost) * 100).toFixed(1)}%</strong>
                                        )
                                        <Tooltip title="Cost this month compared to last months' average total cost" arrow>
                                            <IconButton size="small">
                                                <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Box>
                </>
            ) : (
                <>
                    <Typography
                        variant="h5"
                        align="center"
                        sx={{ fontWeight: 'bold' }}
                        className={'fancyText'}
                    >
                        Key Metrics
                    </Typography>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 320, width: '100%' }}>
                        <HouseMonitoringMetricsBoxes id={id} />
                    </Box>
                </>
            )}
            {/* Pagination Dots & Chevrons */}
            <Box sx={{ width: '100%', position: 'absolute', bottom: 16, left: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', pointerEvents: 'none' }}>
                {[0, 1].map((idx) => (
                    <Box
                        key={idx}
                        onClick={() => handleDotClick(idx)}
                        sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: viewIndex === idx ? theme.palette.secondary.main : theme.palette.grey[300],
                            mx: 0.5,
                            transition: 'background-color 0.3s',
                            cursor: 'pointer',
                            pointerEvents: 'auto',
                        }}
                    />
                ))}
            </Box>
            <Box sx={{ position: 'absolute', bottom: 12, right: 16, display: 'flex', alignItems: 'center', zIndex: 2 }}>
                <IconButton
                    onClick={() => handleToggleView('left')}
                    aria-label="previous view"
                    size="small"
                    disabled={viewIndex === 0}
                    sx={{ mx: 0.5 }}
                >
                    <ChevronLeftIcon />
                </IconButton>
                <IconButton
                    onClick={() => handleToggleView('right')}
                    aria-label="next view"
                    size="small"
                    disabled={viewIndex === 1}
                    sx={{ mx: 0.5 }}
                >
                    <ChevronRightIcon />
                </IconButton>
            </Box>
            <Box sx={{ mb: 5 }} />
        </Paper>
    );
}

export default HouseMonitoringAdditional;
